import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface LockAppStatusData {
  serial_number: string;
  timestamp: string | null;
  source: string | null;
}

interface DeviceWithLockStatus {
  id: string;
  serial_number: string;
  device_name: string | null;
  owner_name: string | null;
  is_locked: boolean;
  lock_message_id?: number | null;
  note: string | null;
  unlock_code: string;
  created_at: string;
  updated_at: string;
  lock_app_status: {
    has_run: boolean;
    timestamp: string | null;
    is_synced: boolean; // true based on lock-state rules and app check-in timing
  };
}

interface LockStatusSummary {
  totalDevices: number;
  lockedInDatabase: number;
  unlockedInDatabase: number;
  lockAppRun: number;
  lockAppNotRun: number;
  syncedDevices: number; // devices considered in sync under lock-check-in rules
  unsyncedDevices: number; // devices considered out of sync under lock-check-in rules
  recentLockActions: Array<{
    serial_number: string;
    device_name: string | null;
    timestamp: string;
    action: 'locked' | 'unlocked';
  }>;
}

export function useLockAppStatus() {
  const [devicesWithStatus, setDevicesWithStatus] = useState<DeviceWithLockStatus[]>([]);
  const [summary, setSummary] = useState<LockStatusSummary>({
    totalDevices: 0,
    lockedInDatabase: 0,
    unlockedInDatabase: 0,
    lockAppRun: 0,
    lockAppNotRun: 0,
    syncedDevices: 0,
    unsyncedDevices: 0,
    recentLockActions: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLockAppStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all devices with their lock app status
      const { data: devicesData, error: devicesError } = await supabase
        .from('devices')
        .select('*')
        .order('created_at', { ascending: false });

      if (devicesError) throw devicesError;

      // Get all lock app runs
      const { data: lockAppData, error: lockAppError } = await supabase
        .from('lock_app_runs')
        .select('*')
        .order('timestamp', { ascending: false });

      if (lockAppError) throw lockAppError;

      // Create a map of serial numbers to their latest lock app status
      const lockAppMap = new Map<string, LockAppStatusData>();
      lockAppData?.forEach(run => {
        if (!lockAppMap.has(run.serial_number)) {
          lockAppMap.set(run.serial_number, {
            serial_number: run.serial_number,
            timestamp: run.timestamp,
            source: run.source
          });
        }
      });

      const parseTimestamp = (value: string | null | undefined): number | null => {
        if (!value) return null;
        const parsed = new Date(value).getTime();
        return Number.isNaN(parsed) ? null : parsed;
      };

      // Combine devices with lock app status
      const devicesWithLockStatus: DeviceWithLockStatus[] = (devicesData || []).map(device => {
        const lockStatus = lockAppMap.get(device.serial_number);
        const has_run = !!lockStatus; // true if record exists in lock_app_runs

        // Sync rules:
        // - Unlocked devices are always considered in sync.
        // - Locked devices are in sync only if lock app has checked in
        //   at or after the lock was issued (approximated using device.updated_at).
        const latestRunTimestamp = parseTimestamp(lockStatus?.timestamp);
        const lockIssuedTimestamp = parseTimestamp(device.updated_at);
        const hasRunSinceLockIssued =
          has_run &&
          latestRunTimestamp !== null &&
          (lockIssuedTimestamp === null || latestRunTimestamp >= lockIssuedTimestamp);

        const is_synced = !device.is_locked || hasRunSinceLockIssued;

        return {
          ...device,
          lock_app_status: {
            has_run,
            timestamp: lockStatus?.timestamp || null,
            is_synced
          }
        };
      });

      // Calculate summary statistics
      const totalDevices = devicesWithLockStatus.length;
      const lockedInDatabase = devicesWithLockStatus.filter(d => d.is_locked).length;
      const unlockedInDatabase = totalDevices - lockedInDatabase;
      const lockAppRun = devicesWithLockStatus.filter(d => d.lock_app_status.has_run).length;
      const lockAppNotRun = totalDevices - lockAppRun;
      const syncedDevices = devicesWithLockStatus.filter(d => d.lock_app_status.is_synced).length;
      const unsyncedDevices = totalDevices - syncedDevices;

      // Get recent lock actions (last 10)
      const recentLockActions = (lockAppData || [])
        .filter(run => run.timestamp) // only include runs with timestamps
        .slice(0, 10)
        .map(run => {
          const device = devicesWithLockStatus.find(d => d.serial_number === run.serial_number);
          return {
            serial_number: run.serial_number,
            device_name: device?.device_name || null,
            timestamp: run.timestamp!,
            action: device?.is_locked ? 'locked' as const : 'unlocked' as const
          };
        });

      setDevicesWithStatus(devicesWithLockStatus);
      setSummary({
        totalDevices,
        lockedInDatabase,
        unlockedInDatabase,
        lockAppRun,
        lockAppNotRun,
        syncedDevices,
        unsyncedDevices,
        recentLockActions
      });

    } catch (err) {
      console.error('Error fetching lock app status:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLockAppStatus();
  }, []);

  return {
    devicesWithStatus,
    summary,
    loading,
    error,
    refetch: fetchLockAppStatus
  };
}
